import { Button, Col, Form, Input, Row, Space, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, createProject, updateProject } from '../../features/projects/projectSlice';
import { ProjectType } from '../../types/types';
import { NotificationContext } from '../../providers/NotificationProvider';
import RiskMatrix from './matrix/MatrixList';
import { FileDoneOutlined, FileExcelOutlined, ForkOutlined } from '@ant-design/icons';
import {
  downloadProjectActionsReport,
  downloadProjectNodesReport,
  downloadProjectFullReport,
} from '../../services/reportService';

const ProjectForm: React.FC = () => {
  const { project_id } = useParams();
  const [isView, setIsView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);
  const [projectBackup, setProjectBackup] = useState<ProjectFormData | null>(null);
  const [form] = Form.useForm();

  interface ProjectFormData {
    name: string;
    customer: string;
    business_unit: string;
    facility: string;
    description: string;
    node_count?: number;
  }

  const analysisButtonLabel = projectBackup?.node_count === 0 ? 'Iniciar Análisis' : 'Ver Análisis';

  useEffect(() => {
    if (project_id && !isNaN(parseInt(project_id))) {
      setIsView(true);
      dispatch(getProject(parseInt(project_id as string)) as any)
        .then((result: any) => {
          if (getProject.fulfilled.match(result)) {
            const project: ProjectFormData = result.payload;
            form.setFieldsValue({
              name: project.name,
              customer: project.customer,
              business_unit: project.business_unit,
              facility: project.facility,
              description: project.description,
            });
            setProjectBackup(project);
          } else {
            triggerNotification('error', 'Error', 'Error al cargar el proyecto', 'bottomRight');
            navigate(`/proyectos/`);
            return;
          }
        })
        .catch((err: any) => {
          triggerNotification('error', 'Error', 'Error al cargar el proyecto', 'bottomRight');
        });
    }
    // eslint-disable-next-line
  }, []);

  const onFinish = async (values: ProjectFormData) => {
    if (isEdit) {
      const oldProject: ProjectType = {
        id: parseInt(project_id as string),
        name: values.name,
        customer: values.customer,
        business_unit: values.business_unit,
        facility: values.facility,
        description: values.description,
      };
      try {
        await dispatch(updateProject(oldProject) as any)
          .then((result: any) => {
            if (updateProject.fulfilled.match(result)) {
              navigate(`/proyectos/`);
            }
          })
          .catch((err: any) => {
            triggerNotification('error', 'Error', 'Error al crear el proyecto', 'bottomRight');
          });
      } catch (error) {
        triggerNotification('error', 'Error', 'Error al crear el proyecto', 'bottomRight');
      }
    } else {
      const newProject: ProjectType = {
        id: null,
        name: values.name,
        customer: values.customer,
        business_unit: values.business_unit,
        facility: values.facility,
        description: values.description,
      };
      try {
        await dispatch(createProject(newProject) as any)
          .then((result: any) => {
            if (createProject.fulfilled.match(result)) {
              navigate(`/proyecto/${result.payload.id}/nodos/`);
            }
          })
          .catch((err: any) => {
            triggerNotification('error', 'Error', 'Error al crear el proyecto', 'bottomRight');
          });
      } catch (error) {
        triggerNotification('error', 'Error', 'Error al crear el proyecto', 'bottomRight');
      }
    }
  };

  const handleDownloadReport = async () => {
    if (!project_id) {
      triggerNotification('error', 'Error', 'Proyecto inválido para exportar', 'bottomRight');
      return;
    }

    try {
      await downloadProjectActionsReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', 'Éxito', 'Descarga del reporte iniciada', 'bottomRight');
    } catch (error) {
      triggerNotification('error', 'Error', 'Error al descargar el reporte', 'bottomRight');
    }
  };

  const handleDownloadNodesReport = async () => {
    if (!project_id) {
      triggerNotification('error', 'Error', 'Proyecto inválido para exportar', 'bottomRight');
      return;
    }

    try {
      await downloadProjectNodesReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', 'Éxito', 'Descarga del reporte de nodos iniciada', 'bottomRight');
    } catch (error) {
      triggerNotification('error', 'Error', 'Error al descargar el reporte de nodos', 'bottomRight');
    }
  };

  const handleDownloadFullReport = async () => {
    if (!project_id) {
      triggerNotification('error', 'Error', 'Proyecto inválido para exportar', 'bottomRight');
      return;
    }

    try {
      await downloadProjectFullReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', 'Éxito', 'Descarga del proyecto completo iniciada', 'bottomRight');
    } catch (error) {
      triggerNotification('error', 'Error', 'Error al descargar el proyecto completo', 'bottomRight');
    }
  };

  return (
    <Row className='content'>
      <Col sm={10}>
        <div className='title-full'>
          <h1>{isView || isEdit ? 'Proyecto' : 'Nuevo Proyecto'} </h1>
          {isView && (
            <Space wrap>
              <Button type='primary' htmlType='button' onClick={() => navigate(`/proyecto/${project_id}/nodos/`)}>
                {analysisButtonLabel}
              </Button>
              <Tooltip title='Exportar Lista de Recomendaciones'>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label='Descargar reporte de acciones'
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<FileExcelOutlined style={{ color: '#fff' }} />}
                  onClick={handleDownloadReport}
                />
              </Tooltip>
              <Tooltip title='Exportar Lista de Nodos'>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label='Descargar lista de nodos'
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<ForkOutlined style={{ color: '#fff' }} />}
                  onClick={handleDownloadNodesReport}
                />
              </Tooltip>
              <Tooltip title='Exportar Proyecto Completo'>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label='Descargar proyecto completo'
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<FileDoneOutlined style={{ color: '#fff' }} />}
                  onClick={handleDownloadFullReport}
                />
              </Tooltip>
            </Space>
          )}
        </div>

        <div className='ant-divider'></div>
        <Form form={form} layout='vertical' onFinish={onFinish}>
          <Form.Item hidden>
            <Input disabled />
          </Form.Item>
          <Form.Item
            label='Nombre del Proyecto'
            name='name'
            rules={[{ required: true, message: 'Por favor ingresa el nombre del proyecto' }]}
          >
            <Input placeholder='Nombre del proyecto' readOnly={isView} />
          </Form.Item>

          <Form.Item label='Cliente' name='customer'>
            <Input placeholder='Nombre del cliente' readOnly={isView} />
          </Form.Item>

          <Form.Item label='Unidad de negocio' name='business_unit'>
            <Input placeholder='Unidad de negocio' readOnly={isView} />
          </Form.Item>

          <Form.Item label='Instalación' name='facility'>
            <Input placeholder='Nombre de la instalación' readOnly={isView} />
          </Form.Item>

          <Form.Item label='Descripción' name='description'>
            <Input.TextArea placeholder='Descripción del proyecto' readOnly={isView} />
          </Form.Item>

          {!isView && (
            <Form.Item>
              <Button type='primary' htmlType='submit'>
                Guardar
              </Button>{' '}
              {isEdit ? (
                <Button
                  type='primary'
                  danger
                  htmlType='button'
                  onClick={() => {
                    form.setFieldsValue(projectBackup);
                    setIsView(true);
                    setIsEdit(false);
                  }}
                >
                  Cancelar
                </Button>
              ) : null}
            </Form.Item>
          )}
        </Form>
        {isView && !isEdit ? (
          <Button
            type='primary'
            htmlType='button'
            onClick={() => {
              setIsView(false);
              setIsEdit(true);
            }}
          >
            Editar
          </Button>
        ) : null}
      </Col>
      <Col sm={2}></Col>
      <Col sm={12}>
        <div className='map-container'>{project_id && <RiskMatrix project_id={project_id} />}</div>
      </Col>
    </Row>
  );
};

export default ProjectForm;
